import { defineStore } from 'pinia';
import type { DemandeDTO, DemandeLigneDTO, ReferentielsDTO } from '@its/shared';
import { api } from '@/lib/api';
import { rafraichirCloche } from '@/lib/cloche';

export interface FiltresFile {
  vue: 'file' | 'miennes';
  onglet?: 'internes' | 'clients';
  etat: 'ouvertes' | 'emise' | 'cloturees' | 'toutes';
  periode: '1m' | '6m' | '1a' | 'tout';
  intervenant?: string;
  /** Code d'un type de demande (`logiciel`, `materiel`…), ou `tous`. */
  type?: string;
}

/**
 * Les demandes. Le squelette de chargement ne s'affiche qu'au PREMIER
 * chargement d'une liste ; ensuite, un changement de filtre ou une mutation
 * remplace les données sur place — pas de clignotement.
 */
export const useDemandesStore = defineStore('demandes', {
  state: () => ({
    referentiels: null as ReferentielsDTO | null,
    lignes: [] as DemandeLigneDTO[],
    charge: false,
    chargement: false,
    courante: null as DemandeDTO | null,
  }),
  actions: {
    async chargerReferentiels(): Promise<ReferentielsDTO> {
      if (!this.referentiels) this.referentiels = await api.get<ReferentielsDTO>('/demandes/referentiels');
      return this.referentiels;
    },
    /** Un type d'intervention a changé dans l'administration : la prochaine saisie relit la liste. */
    oublierReferentiels(): void {
      this.referentiels = null;
    },

    async lister(f: FiltresFile): Promise<void> {
      this.chargement = !this.charge;
      const q = new URLSearchParams({ vue: f.vue, etat: f.etat, periode: f.periode });
      if (f.onglet) q.set('onglet', f.onglet);
      if (f.intervenant && f.intervenant !== 'tous') q.set('intervenant', f.intervenant);
      if (f.type && f.type !== 'tous') q.set('type', f.type);
      try {
        this.lignes = await api.get<DemandeLigneDTO[]>(`/demandes?${q}`);
        this.charge = true;
      } finally {
        this.chargement = false;
      }
    },

    async ouvrir(id: string): Promise<DemandeDTO> {
      this.courante = await api.get<DemandeDTO>(`/demandes/${id}`);
      // Ouverte par son preneur en charge, elle est lue : la cloche se recharge APRÈS.
      rafraichirCloche();
      return this.courante;
    },

    /** Toute action sur la fiche rend la fiche à jour : on la remplace sur place. */
    async agir(id: string, chemin: string, corps: unknown): Promise<DemandeDTO> {
      this.courante = await api.post<DemandeDTO>(`/demandes/${id}/${chemin}`, corps);
      rafraichirCloche();
      return this.courante;
    },
  },
});
